/**
 * Live-backend integration for the hospitality adapter.
 *
 * The adapter persists ONLY config (hosp_location / hosp_section / hosp_floor_table /
 * hosp_setting) and streams live KPI counters over SSE; every other slice is transactional
 * and owned by the SK backbone (simulated locally from the prototype seed). So this module:
 *   1. loads config from /v1 if the venue already exists, else seeds the prototype config;
 *   2. merges the loaded/seeded config with the prototype's transactional overlay (status,
 *      party, since) so the floor plan looks exactly like the mockup;
 *   3. opens the dashboard SSE stream and feeds counters into the store;
 *   4. writes config + new reservations back to the adapter (debounced, best-effort).
 *
 * Everything is guarded: if the backend is down / the session is unauthenticated, the app
 * silently keeps the seeded prototype state and stays fully interactive for the demo.
 */

import { getLocations, updateLocation } from './config/location';
import { syncPlatformLocations } from './platform/locations';
import type { Location } from '../schemas/config/location';
import { getSections } from './config/section';
import { getFloorTables } from './config/table';
import { getSettings, upsertSetting } from './config/setting';
import { getReservations } from './reservations/reservation';
import { getTickets } from './support/ticket';
import { getGuestsBatch } from './crm/guest';
import { listStaff } from './people/staff';
import { openDashboardStream } from './dashboard/dashboard';
import { generateBill } from './billing/billing';
import { getTodaysOrders } from './orders/order';
import type { FloorTable, TableShape as ApiShape } from '../schemas/config/table';
import { useHospitalityStore } from '../store/useHospitalityStore';
import { useAppStore } from '../store/useAppStore';
import type { FloorTableState, Order, OrderLine, SectionCfg, SettingsCfg, TableShape } from '../data/seed';
import type { TableStatus } from '../theme/constants';
import { orderStatusFromDoc } from '../data/derive';

// adapter shape (square | circle | rectangle) → app shape
const fromApiShape = (s: ApiShape): TableShape => (s === 'circle' ? 'round' : s === 'rectangle' ? 'communal' : 'square');
const shapeSize = (s: TableShape): { w: number; h: number } => (s === 'communal' ? { w: 142, h: 74 } : s === 'round' ? { w: 56, h: 56 } : { w: 74, h: 74 });

interface BillingDoc {
  id: string;
  status?: string;
  subtotal?: string;
  tax_total?: string;
  grand_total?: string;
  lines?: { id?: string; description: string; quantity?: string; unit_price?: string }[];
}

/** Rebuild a local Order (for the Orders & Upsell screen) from a REAL billing
 *  document — the rehydration counterpart to createOrder/addItem, which is
 *  what wrote this document in the first place. Never invents numbers: every
 *  field here is read straight off what billing actually has — including
 *  status, so a bill that was actually sent (ISSUED+) before reload doesn't
 *  revert to "Open" just because the page refreshed. */
function orderFromBillingDoc(tableLabel: string, guests: number, server: string, doc: BillingDoc): Order {
  const lines: OrderLine[] = (doc.lines ?? []).map((l, i) => ({
    id: l.id ?? `l-${doc.id}-${i}`,
    name: l.description,
    qty: Math.round(Number(l.quantity ?? 1)),
    price: Number(l.unit_price ?? 0),
  }));
  return {
    id: doc.id,
    table: tableLabel,
    guests,
    server,
    status: orderStatusFromDoc(doc.status),
    accepted: false,
    declined: false,
    upsell: null,
    lines,
    subtotal: Number(doc.subtotal ?? 0),
    taxTotal: Number(doc.tax_total ?? 0),
    grandTotal: Number(doc.grand_total ?? 0),
  };
}

let started = false;

export async function bootstrapHospitality(): Promise<void> {
  if (started) return;
  started = true;
  try {
    // orgName/orgId are set at the auth gate from /v1/me (platform is the SSOT).
    // Seed the Venue form's read-only Organisation field now so it shows the real
    // org even before any venue exists (loadExistingConfig only runs when one does).
    const orgName = useAppStore.getState().orgName;
    if (orgName) useHospitalityStore.getState().setLocation('org', orgName);

    // The adapter's "Create venue" call must supply the platform's own location
    // id (hosp mints none of its own) — sync it once so it's ready before the
    // operator ever opens the Venue form. This ALSO references the location
    // into hosp_location (see settings.service.syncLocationsFromPlatform), so
    // getLocations() below already finds it on a fresh org. Best-effort: if
    // this fails, "Create venue" will surface the real 400 from the adapter
    // instead of silently omitting the field.
    if (!useAppStore.getState().platformLocationId) {
      try {
        const platformLocations = await syncPlatformLocations();
        const primary = platformLocations.find((l) => l.isPrimary) ?? platformLocations[0];
        if (primary) useAppStore.setState({ platformLocationId: primary.id });
      } catch { /* leave blank — create-venue will surface the real error */ }
    }

    const locations = await getLocations();
    const existing = locations[0]; // load this operator's first venue, if any

    if (existing) {
      await loadExistingConfig(existing);
    }
    // No venue yet → nothing to load; the operator creates config through the UI
    // (installWriteThrough persists it). No mock config is seeded into the backend.
    startCounters();
    installWriteThrough();
  } catch {
    // Backend unreachable or unauthenticated — the app stays on its empty state.
    started = false;
  }
}

/**
 * Re-pull the "Tonight" list from the adapter (skcal is the source of truth) and
 * refresh the store, resolving canonical CRM names. Call after a booking/cancel so
 * rows — including the specific allocated table — reflect the live calendar without
 * a page reload.
 */
export async function refreshReservations(): Promise<void> {
  const apiReservations = await getReservations().catch(() => []);
  const ids = apiReservations.map((r) => r.contactId).filter((x): x is string => !!x);
  const guestsById: Record<string, { name: string }> = ids.length
    ? await getGuestsBatch(ids).catch(() => ({}))
    : {};
  const resolved = apiReservations.map((r) => (r.contactId && guestsById[r.contactId] ? { ...r, name: guestsById[r.contactId].name } : r));
  useHospitalityStore.getState().setReservations(resolved);
  // Reflect the current booking state on the floor — a table skcal has actually
  // confirmed a reservation against blocks from walk-in seating; one that's been
  // cancelled (no longer in the list) frees back up.
  useHospitalityStore.getState().applyReservationHolds(resolved);
}

// ── load the venue's config from the adapter (no mock overlay) ──
async function loadExistingConfig(location: Location): Promise<void> {
  const locationId = location.id;
  const [apiSections, apiTables, apiSettings, apiReservations, apiTickets, staff] = await Promise.all([
    getSections(locationId).catch(() => []),
    getFloorTables(locationId).catch(() => [] as FloorTable[]),
    getSettings(locationId).catch(() => []),
    getReservations().catch(() => []),
    getTickets().catch(() => []),
    listStaff().catch(() => []), // SK People — resolves each section's serverRef to a real name
  ]);
  const staffById = new Map(staff.map((e) => [e.id, e.name]));

  const sections: SectionCfg[] = apiSections.map((sec) => ({
    id: sec.id, apiId: sec.id, name: sec.name,
    prefix: sec.name[0]?.toUpperCase() ?? 'S',
    server: sec.serverRef ? staffById.get(sec.serverRef) ?? '' : '',
    serverRef: sec.serverRef ?? '',
  }));

  // Real live state (status/party/seatedSince/activeOrderId/activeEntryId) —
  // written by the seat/order/settle actions themselves (see
  // reservations.service.ts / orders.service.ts on the adapter). Reading it
  // back here is what makes a reload (or a second staff device) show the
  // floor as it actually is, instead of starting blank every time.
  const tables: FloorTableState[] = apiTables.map((t) => {
    const shape = fromApiShape(t.shape);
    const size = shapeSize(shape);
    const section = apiSections.find((sec) => sec.id === t.sectionId)?.name ?? '';
    const server = sections.find((sec) => sec.name === section)?.server ?? '';
    return {
      id: t.id, apiId: t.id, label: t.label, section,
      server, seats: t.seats, shape,
      x: t.posX, y: t.posY, w: size.w, h: size.h,
      status: (t.status as TableStatus) || 'free',
      party: t.party ?? null,
      since: t.seatedSince ? new Date(t.seatedSince).toTimeString().slice(0, 5) : null,
      entryId: t.activeEntryId ?? null,
      orderId: t.activeOrderId ?? null,
      combinable: t.combinable, communal: t.communal,
    };
  });

  // Any table with a real open order → pull its actual bill and rebuild a
  // local Order for it, so Orders & Upsell shows the real lines/total again
  // instead of "No open orders yet." (order.id was only ever in browser
  // memory before; activeOrderId is what survives a reload now.)
  const orders = (
    await Promise.all(
      apiTables
        .filter((t) => t.activeOrderId)
        .map(async (t) => {
          try {
            const doc = (await generateBill(t.activeOrderId as string)) as BillingDoc;
            const sectionName = apiSections.find((sec) => sec.id === t.sectionId)?.name;
            const server = sections.find((sec) => sec.name === sectionName)?.server ?? '';
            return orderFromBillingDoc(t.label, t.seats, server, doc);
          } catch {
            return null; // stale/deleted document — table shows the order gone, not a crash
          }
        }),
    )
  ).filter((o): o is Order => o !== null);

  // Merge in any other real order dated today that isn't already covered
  // above — i.e. its table has since been freed, so the floor-table walk
  // above can no longer find it (activeOrderId was cleared). Without this,
  // a finished bill becomes unrecoverable the moment its table frees, even
  // though the real billing document still exists. `table`/`guests` come
  // back from the document's own notes field (see orders.service.ts's
  // encodeOrderNotes) — the one place that survives past the table forgetting.
  try {
    const todays = await getTodaysOrders();
    const knownIds = new Set(orders.map((o) => o.id));
    for (const doc of todays) {
      if (knownIds.has(doc.id)) continue;
      orders.push({
        id: doc.id,
        table: doc.table ?? 'Unknown table',
        guests: doc.guests ?? null,
        server: '',
        status: orderStatusFromDoc(doc.status),
        accepted: false,
        declined: false,
        upsell: null,
        lines: (doc.lines ?? []).map((l, i) => ({
          id: l.id ?? `l-${doc.id}-${i}`,
          name: l.description,
          qty: Math.round(Number(l.quantity ?? 1)),
          price: Number(l.unit_price ?? 0),
        })),
        subtotal: Number(doc.subtotal ?? 0),
        taxTotal: Number(doc.tax_total ?? 0),
        grandTotal: Number(doc.grand_total ?? 0),
      });
    }
  } catch {
    // best-effort — history enrichment failing shouldn't block the rest of load
  }

  const settingsPatch = settingsFromApi(apiSettings);
  // Hydrate the Venue form from the persisted row (incl. apiId) so edits PATCH the
  // existing venue instead of POSTing a new one. The adapter returns operating hours
  // as ISO datetimes (1970-01-01T11:00:00Z); the form wants HH:MM.
  const hhmm = (iso: string) => (iso.includes('T') ? iso.slice(11, 16) : iso);
  // Organisation = the tenant's display name from platform /v1/me (org identity SSOT).
  // It's not persisted by the adapter (only name/tz/hours are) — it identifies the
  // tenant and drives the Calendar resource-id preview.
  const locationCfg = {
    apiId: location.id,
    org: useAppStore.getState().orgName,
    name: location.name,
    timezone: location.timezone,
    open: hhmm(location.operatingOpen),
    close: hhmm(location.operatingClose),
  };
  useHospitalityStore.getState().hydrateConfig({ location: locationCfg, sections, tables, settings: settingsPatch });
  useHospitalityStore.getState().hydrateOrders(orders);
  // Reservations live in skcal (the adapter owns no booking state); seed the
  // "Tonight" list from the backend so it survives a refresh. `time` is ISO here —
  // the Reservations screen formats it for display.
  // Resolve guest names from sk-CRM (live, deduped) for rows that carry a contactId,
  // so the list shows the canonical name — one batch call, not N.
  const resContactIds = apiReservations.map((r) => r.contactId).filter((x): x is string => !!x);
  const guestsById: Record<string, { name: string }> = resContactIds.length
    ? await getGuestsBatch(resContactIds).catch(() => ({}))
    : {};
  const resolvedReservations = apiReservations.map((r) => (r.contactId && guestsById[r.contactId] ? { ...r, name: guestsById[r.contactId].name } : r));
  useHospitalityStore.getState().setReservations(resolvedReservations);
  // Same reconciliation as refreshReservations — a fresh load must show
  // already-reserved tables as blocked, not free, on first paint.
  useHospitalityStore.getState().applyReservationHolds(resolvedReservations);
  // Tickets live in sk-tickets; seed the Support list. Guest names are already
  // resolved from sk-CRM inside getTickets() (reference-not-copy). List rows carry
  // no thread yet (fetched on open); fill store-required fields with empty defaults.
  useHospitalityStore.getState().setTickets(
    apiTickets.map((t) => ({ ...t, by: '', timeline: [] })),
  );
  useHospitalityStore.getState().markBackendLive(locationId);
}

// A venue that saved turn_times before reservedLeadMinutes existed has a JSON
// blob missing that key — hydrateConfig's settings merge replaces `timing`
// wholesale (shallow merge), so without this fallback the field would come
// back `undefined` (and Math.max(0, undefined * 60000) = NaN, which makes the
// "is this reservation too far out" check always false — every confirmed
// reservation would show Reserved regardless of how far away it is).
function withTimingDefaults(v: Partial<SettingsCfg['timing']>): SettingsCfg['timing'] {
  return {
    diningSmall: v.diningSmall ?? 75,
    diningGroup: v.diningGroup ?? 105,
    cleaningBuffer: v.cleaningBuffer ?? 15,
    bookingGap: v.bookingGap ?? 0,
    reservedLeadMinutes: v.reservedLeadMinutes ?? 30,
  };
}

function settingsFromApi(rows: { key: string; value: unknown }[]): Partial<SettingsCfg> {
  const patch: Partial<SettingsCfg> = {};
  for (const row of rows) {
    if (row.key === 'combine_policy' && typeof row.value === 'string') patch.combinePolicy = row.value as SettingsCfg['combinePolicy'];
    if (row.key === 'upsell_rule_ref' && Array.isArray(row.value)) patch.upsellRules = row.value as SettingsCfg['upsellRules'];
    if (row.key === 'dashboard_view' && typeof row.value === 'string') patch.dashboardView = row.value;
    if (row.key === 'tier3_toggles' && row.value && typeof row.value === 'object') {
      const v = row.value as { service?: SettingsCfg['service']; timing?: SettingsCfg['timing'] };
      if (v.service) patch.service = v.service;
      // Back-compat: timing used to be bundled here before it got its own key.
      if (v.timing && !patch.timing) patch.timing = withTimingDefaults(v.timing);
    }
    // turn_times is the dedicated home for the Timing & turns tab (overrides legacy).
    if (row.key === 'turn_times' && row.value && typeof row.value === 'object') {
      patch.timing = withTimingDefaults(row.value as Partial<SettingsCfg['timing']>);
    }
  }
  return patch;
}

async function pushAllSettings(locationId: string, settings: SettingsCfg): Promise<void> {
  const puts: Promise<unknown>[] = [
    upsertSetting({ locationId, key: 'combine_policy', value: settings.combinePolicy }),
    upsertSetting({ locationId, key: 'upsell_rule_ref', value: settings.upsellRules }),
    upsertSetting({ locationId, key: 'tier3_toggles', value: { service: settings.service } }),
    upsertSetting({ locationId, key: 'turn_times', value: settings.timing }),
    upsertSetting({ locationId, key: 'dashboard_view', value: settings.dashboardView }),
  ];
  await Promise.all(puts.map((p) => p.catch(() => undefined)));
}

// ── dashboard SSE → KPI counters ──
function startCounters(): void {
  try {
    openDashboardStream((c) => {
      useHospitalityStore.setState((s) => {
        // Revenue "goes live" the first time a real, positive amount arrives — until
        // then keep the sim animating so a freshly-wired but payment-less backend
        // doesn't just freeze the tile at ₹0. Once live, trust every SSE value.
        const revenueLive = s.revenueLive || (c.revenueToday ?? 0) > 0;
        return {
          callsLive: c.callsLive ?? s.callsLive,
          ordersToday: c.orderCount ?? s.ordersToday,
          ordersOnWay: c.ordersOnTheWay ?? s.ordersOnWay,
          // Trusts the backend's live count over this session's own `tables` array,
          // so a table seated/freed on another terminal shows up here without a reload.
          tablesOccupied: c.tablesOccupied ?? s.tablesOccupied,
          upsellPct: c.upsellOffered ? Math.round(((c.upsellAccepted ?? 0) / c.upsellOffered) * 100) : s.upsellPct,
          revenueToday: revenueLive ? (c.revenueToday ?? s.revenueToday) : s.revenueToday,
          revenueLive,
          // A legitimate transition is object → null (VOICE's draft resolved) —
          // `c.draft ?? s.draft` would treat that null as "absent" and keep the
          // stale card forever, so check presence explicitly instead.
          draft: 'draft' in c ? c.draft : s.draft,
        };
      });
      useHospitalityStore.getState().persistDash();
    });
  } catch { /* SSE unsupported / blocked — sim keeps counters moving */ }
}

// ── config + reservation write-through (debounced, best-effort) ──
function debounce<T extends (...a: never[]) => void>(fn: T, ms: number): T {
  let h: ReturnType<typeof setTimeout> | null = null;
  return ((...a: never[]) => { if (h) clearTimeout(h); h = setTimeout(() => fn(...a), ms); }) as T;
}

function installWriteThrough(): void {
  const pushConfig = debounce(() => {
    const s = useHospitalityStore.getState();
    if (!s.backendLive || !s.apiLocationId) return;
    updateLocation(s.apiLocationId, {
      name: s.location.name, timezone: s.location.timezone, operatingOpen: s.location.open, operatingClose: s.location.close,
    }).catch(() => undefined);
    pushAllSettings(s.apiLocationId, s.settings).catch(() => undefined);
  }, 900);

  let lastLocation = useHospitalityStore.getState().location;
  let lastSettings = useHospitalityStore.getState().settings;

  // Config-only write-through. Reservations are NOT written here: the Reservations
  // screen is the single writer (it POSTs and reconciles the real Calendar id).
  // A second writer here would double-book every reservation.
  useHospitalityStore.subscribe((s) => {
    if (s.location !== lastLocation || s.settings !== lastSettings) {
      lastLocation = s.location;
      lastSettings = s.settings;
      pushConfig();
    }
  });
}
